import React, { useRef, useState } from 'react';
import {
  ClickAwayListener,
  Divider,
  IconButton,
  Paper,
  Popper,
  Stack,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { Controller, useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { InputWrapper } from './InputWrapper';
import { IconWrapper } from 'components/Icon';

const pad2 = (value) => String(value).padStart(2, '0');

const toValidDayjs = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : dayjs();
};

const spinnerIconSx = { width: 14, height: 14, color: 'inherit' };

const TimeSpinner = ({ label, value, onIncrement, onDecrement, onWheel, disabled }) => (
  <Stack alignItems="center" gap={0} onWheel={onWheel}>
    <IconButton
      size="small"
      onClick={onIncrement}
      disabled={disabled}
      aria-label={`Increase ${label}`}
      sx={{ p: 0.25, color: 'grey.500' }}
    >
      <ChevronUpIcon style={spinnerIconSx} />
    </IconButton>
    <Typography
      variant="h6"
      sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 32, textAlign: 'center', lineHeight: 1, my: 0.5 }}
    >
      {pad2(value)}
    </Typography>
    <IconButton
      size="small"
      onClick={onDecrement}
      disabled={disabled}
      aria-label={`Decrease ${label}`}
      sx={{ p: 0.25, color: 'grey.500' }}
    >
      <ChevronDownIcon style={spinnerIconSx} />
    </IconButton>
  </Stack>
);

export function DateTimeInput({
  name,
  label,
  labelSx = {},
  fullWidth = false,
  disabled = false,
  required,
  wrapperSx = {},
  minDate,
  maxDate,
  value,
  onChange,
}) {
  const { control } = useFormContext();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const currentValue = toValidDayjs(value !== undefined ? value : field.value);
        const emitChange = onChange || field.onChange;

        const handleDateChange = (newDate) => {
          if (!newDate || !dayjs(newDate).isValid()) return;
          emitChange(
            dayjs(newDate).hour(currentValue.hour()).minute(currentValue.minute()).second(0).millisecond(0)
          );
        };

        const changeHour = (delta) => {
          emitChange(currentValue.hour((currentValue.hour() + delta + 24) % 24).second(0).millisecond(0));
        };

        const changeMinute = (delta) => {
          emitChange(currentValue.minute((currentValue.minute() + delta + 60) % 60).second(0).millisecond(0));
        };

        const handleClose = () => setOpen(false);

        return (
          <InputWrapper
            error={!!fieldState?.error}
            label={label}
            fullWidth={fullWidth}
            required={required}
            labelSx={labelSx}
            wrapperSx={wrapperSx}
          >
            <MuiTextField
              ref={anchorRef}
              fullWidth
              disabled={disabled}
              error={!!fieldState.error}
              helperText={fieldState.error?.message || ''}
              value={currentValue.format('YYYY-MM-DD HH:mm')}
              onClick={() => !disabled && setOpen(true)}
              InputProps={{
                readOnly: true,
                endAdornment: <IconWrapper icon="calendar" height={20} />,
              }}
              sx={{ cursor: 'pointer', '& .MuiInputBase-input': { cursor: 'pointer' } }}
            />
            <Popper
              open={open}
              anchorEl={anchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
              onKeyDown={(event) => {
                if (event.key === 'Escape') handleClose();
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <Paper
                  sx={{
                    mt: 0.5,
                    display: 'flex',
                    border: (theme) => `solid 1px ${theme.palette.divider}`,
                    boxShadow: (theme) => theme.shadows[8],
                  }}
                >
                  <DateCalendar
                    value={currentValue}
                    onChange={handleDateChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    disabled={disabled}
                  />
                  <Divider orientation="vertical" flexItem />
                  <Stack direction="row" gap={0.5} alignItems="center" justifyContent="center" px={2} py={1.5}>
                    <TimeSpinner
                      label="hours"
                      value={currentValue.hour()}
                      onIncrement={() => changeHour(1)}
                      onDecrement={() => changeHour(-1)}
                      onWheel={(event) => changeHour(event.deltaY < 0 ? 1 : -1)}
                      disabled={disabled}
                    />
                    <Typography variant="h6">:</Typography>
                    <TimeSpinner
                      label="minutes"
                      value={currentValue.minute()}
                      onIncrement={() => changeMinute(1)}
                      onDecrement={() => changeMinute(-1)}
                      onWheel={(event) => changeMinute(event.deltaY < 0 ? 1 : -1)}
                      disabled={disabled}
                    />
                  </Stack>
                </Paper>
              </ClickAwayListener>
            </Popper>
          </InputWrapper>
        );
      }}
    />
  );
}
